import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { entryFind } from '../../../api/endpoints/reports';
import type { EntryFindRequest } from '../../../api/types/reports';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { ReservationIdentifierLink } from '../../../components/ReservationIdentifierLink';

const emptyForm: EntryFindRequest = {
  pnr: '',
  ticket: '',
  surname: '',
  identity: '',
  email: '',
  gsm: '',
  shoppingCartId: '',
};

/** Drops empty-string fields so the request only carries what the admin actually
 *  filled in — `Entry/Find` requires at least one non-empty param (confirmed via curl,
 *  see api/types/air.ts file header: an all-empty body returns "En az bir parametre
 *  gönderilmeli."). */
function toRequestBody(form: EntryFindRequest): EntryFindRequest {
  const entries = Object.entries(form).filter(([, v]) => !!v && String(v).trim() !== '');
  return Object.fromEntries(entries) as EntryFindRequest;
}

/**
 * "Kayıt Arama" — `Entry/Find` (api/endpoints/reports.ts — moved out of api/endpoints/
 * air.ts once confirmed it's a generic `Report`-tagged route, not Air-specific). Response
 * shape is CONFIRMED: identical envelope/row shape to `Report/Reservations`
 * (`{ reservationReports: ReservationReportRow[] }`), confirmed via live curl with a
 * real `pnr` and a real `surname`. Rendered as a plain MUI Table (no @mui/x-data-grid —
 * incompatible with this project's pinned TypeScript 4.9.5). No inline "Detay" — the PNR
 * (or ticket number) is a link into `ReservationDetailPage`, matching every other
 * reservation list in the app.
 */
export default function AirEntryFindPage() {
  const [form, setForm] = useState<EntryFindRequest>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const searchMutation = useMutation({
    mutationFn: (body: EntryFindRequest) => entryFind(body),
  });

  const rows = searchMutation.data?.reservationReports ?? [];
  const hasSearched = searchMutation.isSuccess || searchMutation.isError;

  const handleField = (field: keyof EntryFindRequest) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = () => {
    setFormError(null);
    const body = toRequestBody(form);
    if (Object.keys(body).length === 0) {
      setFormError('En az bir arama kriteri girmelisiniz (PNR, bilet, soyad, kimlik, e-posta, GSM veya ShoppingCartId).');
      return;
    }
    searchMutation.mutate(body);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Kayıt Arama
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        `Entry/Find` — PNR, bilet numarası, soyad, kimlik/pasaport no, e-posta, GSM veya ShoppingCartId ile arayın. En
        az bir alan doldurulmalıdır.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {formError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Stack direction="row" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField label="PNR" size="small" value={form.pnr} onChange={handleField('pnr')} />
          <TextField label="Bilet No" size="small" value={form.ticket} onChange={handleField('ticket')} />
          <TextField label="Soyad" size="small" value={form.surname} onChange={handleField('surname')} />
          <TextField label="Kimlik / Pasaport No" size="small" value={form.identity} onChange={handleField('identity')} />
          <TextField label="E-posta" size="small" value={form.email} onChange={handleField('email')} />
          <TextField label="GSM" size="small" value={form.gsm} onChange={handleField('gsm')} />
          <TextField
            label="ShoppingCartId"
            size="small"
            value={form.shoppingCartId}
            onChange={handleField('shoppingCartId')}
          />
        </Stack>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" disabled={searchMutation.isPending} onClick={handleSubmit}>
            {searchMutation.isPending ? 'Aranıyor…' : 'Ara'}
          </Button>
        </Box>
        <ApiErrorAlert error={searchMutation.error} sx={{ mt: 2 }} />
      </Paper>

      {hasSearched && !searchMutation.isError && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PNR</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Sağlayıcı</TableCell>
                <TableCell>Yolcu</TableCell>
                <TableCell>Güzergah</TableCell>
                <TableCell>Toplam Ücret</TableCell>
                <TableCell>Rezervasyon Tarihi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Sonuç bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow key={(row.shoppingCartId ?? 'row') + index} hover>
                  <TableCell>
                    <ReservationIdentifierLink row={row} detailBasePath="/air/reservations" />
                  </TableCell>
                  <TableCell>{row.statu || '—'}</TableCell>
                  <TableCell>{row.provider || '—'}</TableCell>
                  <TableCell>{[row.firstName, row.lastName].filter(Boolean).join(' ') || '—'}</TableCell>
                  <TableCell>{[row.origin, row.destination].filter(Boolean).join(' → ') || '—'}</TableCell>
                  <TableCell>{row.totalFare != null ? row.totalFare.toLocaleString('tr-TR') : '—'}</TableCell>
                  <TableCell>{row.bookedAt || row.prebookedAt || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
